// Anke, 2026-07-16 — SVG-Export: die Geometrie.
//
// Hier steht das, was sich ohne Browser rechnen laesst: Zahlen zu SVG-Pfaden.
// Kein DOM, kein Konva-Knoten — damit die Kurven-Mathematik pruefbar bleibt.

/** Kuerzt Gleitkomma-Rauschen; 3 Nachkommastellen sind fuer Vektorgrafik genug. */
export function num(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

/** Maskiert die fuenf Zeichen, die in XML-Text/Attributen nicht roh stehen duerfen. */
export function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Polylinie als SVG-Pfad (`M … L …`).
 * `points` ist die flache Konva-Form [x0,y0,x1,y1,…].
 */
export function polylinePath(points: readonly number[]): string {
  if (points.length < 4) return '';
  const parts: string[] = [`M ${num(points[0]!)} ${num(points[1]!)}`];
  for (let i = 2; i < points.length; i += 2) {
    parts.push(`L ${num(points[i]!)} ${num(points[i + 1]!)}`);
  }
  return parts.join(' ');
}

/**
 * Kardinal-Spline (Konvas `tension`) als SVG-Bezier-Pfad.
 *
 * Konva zeichnet eine Kurve NICHT als Bezier, sondern als Kardinal-Spline: Die
 * Punkte sind Stuetzstellen, durch die die Kurve LAEUFT — keine Bezier-Griffe.
 * Damit der Export exakt dasselbe zeigt wie der Bildschirm, wird hier dieselbe
 * Umrechnung benutzt, die auch der Renderer verwendet (`splineControlPoints`
 * wird von aussen hereingereicht, damit diese Datei Konva-frei bleibt).
 *
 * Die Endstuecke sind quadratisch (`Q`), die Mittelstuecke kubisch (`C`) —
 * genau wie Konva es zeichnet.
 */
export function splinePath(
  points: readonly number[],
  tension: number,
  controlPointsOf: (
    x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, t: number,
  ) => { in: [number, number]; out: [number, number] } | null,
): string {
  const n = points.length / 2;
  if (n < 2) return '';
  if (n === 2 || tension === 0) return polylinePath(points);

  const px = (i: number): number => points[i * 2]!;
  const py = (i: number): number => points[i * 2 + 1]!;

  // Fuer jeden INNEREN Punkt das Bezier-Griffpaar bestimmen.
  const handles: (({ in: [number, number]; out: [number, number] }) | null)[] = [];
  for (let i = 1; i < n - 1; i++) {
    handles[i] = controlPointsOf(
      px(i - 1), py(i - 1), px(i), py(i), px(i + 1), py(i + 1), tension,
    );
  }

  const out: string[] = [`M ${num(px(0))} ${num(py(0))}`];
  for (let i = 0; i < n - 1; i++) {
    const startOut = i === 0 ? null : handles[i]?.out ?? null;
    const endIn = i + 1 === n - 1 ? null : handles[i + 1]?.in ?? null;
    const ex = px(i + 1);
    const ey = py(i + 1);

    if (startOut && endIn) {
      out.push(`C ${num(startOut[0])} ${num(startOut[1])} ${num(endIn[0])} ${num(endIn[1])} ${num(ex)} ${num(ey)}`);
    } else if (endIn) {
      // Erstes Stueck: nur der Griff am Zielpunkt → quadratisch.
      out.push(`Q ${num(endIn[0])} ${num(endIn[1])} ${num(ex)} ${num(ey)}`);
    } else if (startOut) {
      // Letztes Stueck: nur der Griff am Startpunkt → quadratisch.
      out.push(`Q ${num(startOut[0])} ${num(startOut[1])} ${num(ex)} ${num(ey)}`);
    } else {
      out.push(`L ${num(ex)} ${num(ey)}`);
    }
  }
  return out.join(' ');
}

/** Achsparalleles Rechteck. */
export interface Box { x: number; y: number; width: number; height: number }

/** Vereinigung mehrerer Rechtecke; `null` bei leerer Liste. */
export function unionBox(boxes: readonly Box[]): Box | null {
  if (boxes.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const b of boxes) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Echte Schriftnamen fuer den Export (Anke, 2026-07-16 — Fehlerbericht
 * "Illustrator: -apple-system, unbekanntes Problem").
 *
 * Am Bildschirm steht in den Schriftlisten `system-ui, -apple-system,
 * sans-serif`. Das sind CSS-SCHLUESSELWOERTER mit der Bedeutung "nimm die
 * Systemschrift" — keine Schriftnamen. Browser verstehen sie; Zeichenprogramme
 * wie Illustrator suchen dagegen nach einer Schrift dieses NAMENS, finden keine
 * und melden einen Fehler (deshalb taucht "apple" auch unter Windows auf).
 *
 * Fuer die exportierte Datei werden sie darum durch echte, breit verfuegbare
 * Namen ersetzt: Segoe UI (Windows), Roboto (Android/Linux), Helvetica (macOS),
 * Arial (ueberall) — und am Ende bleibt die allgemeine Angabe `sans-serif`
 * stehen, damit auch ohne all diese Schriften etwas Vernuenftiges erscheint.
 *
 * Die Anzeige bleibt unberuehrt: Das hier laeuft nur ueber die Ausgabe.
 */
const PORTABLE_FONT_LEAD = `'Segoe UI', Roboto, Helvetica, Arial`;

/** Portabler Ersatz fuer die System-Schriftliste — auch fuer eigene `<text>`. */
export const PORTABLE_FONT_FAMILY = `${PORTABLE_FONT_LEAD}, sans-serif`;

/**
 * Ersetzt CSS-eigene System-Schluesselwoerter im fertigen SVG durch echte
 * Schriftnamen. Laeuft ueber den GESAMTEN Text, damit auch Schriftangaben
 * erwischt werden, die tief in eingebettetem HTML stehen.
 */
export function toPortableFonts(svg: string): string {
  return svg
    // Reine CSS-Schluesselwoerter ersatzlos streichen (samt folgendem Komma).
    .replace(/-apple-system\s*,\s*/g, '')
    .replace(/BlinkMacSystemFont\s*,\s*/g, '')
    .replace(/ui-sans-serif\s*,\s*/g, '')
    // ... und das fuehrende `system-ui` durch die echten Namen ersetzen.
    .replace(/\bsystem-ui\b/g, PORTABLE_FONT_LEAD);
}

/** Attribut nur ausgeben, wenn der Wert gesetzt ist — haelt das SVG lesbar. */
export function attr(name: string, value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') return '';
  return ` ${name}="${typeof value === 'number' ? num(value) : esc(String(value))}"`;
}
